/** All Customers (Demo Mode only) - full customer directory for the Customer Management
 * workspace. A simpler browse/search view than the Customer Hub dashboard (`../CustomerHub`) -
 * no status/risk analysis, just the portfolio list with quick access into each customer's
 * workspace. All figures are static mock data, matching the "seeded illustrative numbers"
 * approach used by `src/pages/EstateManagement`. */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, Grid, IconButton, InputAdornment, SvgIcon, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, useTheme,
} from '@mui/material';
import {
  People, Store, Category, Business, Clear, Search, ArrowForward, Add,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import CustomPagination from '../../../components/forms/Pagination';
import { getLiveCustomers } from '../../../mocks/data/customerManagementData';

const AllCustomers = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  // Only operational (Live/Optimising) customers - customers still onboarding live on the
  // Onboarding Pipeline (`/customer-management/onboarding-pipeline`) instead.
  const customers = getLiveCustomers();

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return customers;
    }
    return customers.filter((customer) => customer.name.toLowerCase().includes(query) || customer.sector.toLowerCase().includes(query) || customer.owner.toLowerCase().includes(query));
  }, [customers, search]);

  const totalSites = customers.reduce((total, customer) => total + customer.sitesCount, 0);
  const sectors = new Set(customers.map((customer) => customer.sector)).size;

  const METRIC_TILES = [
    { label: t('allCustomersTotal', 'Total clients'), value: `${customers.length}`, subtitle: t('allCustomersTotalSub', 'Full portfolio'), icon: <People />, color: '#1976d2' },
    { label: t('allCustomersSectors', 'Sectors served'), value: `${sectors}`, subtitle: t('allCustomersSectorsSub', 'Diversified portfolio'), icon: <Category />, color: '#d32f2f' },
    { label: t('allCustomersSites', 'Total locations'), value: `${totalSites}`, subtitle: t('allCustomersSitesSub', 'Operated across clients'), icon: <Store />, color: '#9c27b0' },
    { label: t('allCustomersOwners', 'Account owners'), value: `${new Set(customers.map((c) => c.owner)).size}`, subtitle: t('allCustomersOwnersSub', 'Managing the portfolio'), icon: <Business />, color: '#2e7d32' },
  ];

  return (
    <PageArea>
      <Box display="flex"
        alignItems="center"
        justifyContent="space-between">
        <PageTitle subTitle={t('customersSubTitle', 'Browse live and optimising clients. Clients still onboarding appear on the Onboarding Pipeline.')}>
          {t('customersTitle', 'Clients')}
        </PageTitle>
        <Button
          variant="primary"
          startIcon={<Add />}
          onClick={() => navigate('/customer-management/customers/new')}
        >
          {t('allCustomersAddCustomer', 'Add Client')}
        </Button>
      </Box>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {METRIC_TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.label}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mb: 4 }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('allCustomersSearchPlaceholder', 'Search clients by name, sector or owner')}
          size="small"
          sx={{ width: 360, mb: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small"
                  onClick={() => setSearch('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Section>
        <TableHeader>
          {t('allCustomersListTitle', 'Client directory')}
          <Pill text={`${filtered.length} ${t('allCustomersCountLabel', 'clients')}`} />
        </TableHeader>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('allCustomersCustomer', 'Client')}</TableCell>
                <TableCell>{t('allCustomersSector', 'Sector')}</TableCell>
                <TableCell>{t('allCustomersOperatingModel', 'Operating Model')}</TableCell>
                <TableCell>{t('allCustomersSites', 'Locations')}</TableCell>
                <TableCell>{t('allCustomersOwner', 'Account Owner')}</TableCell>
                <TableCell>{t('allCustomersStatus', 'Status')}</TableCell>
                <TableCell>{t('allCustomersActions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((customer) => (
                <TableRow key={customer.id}
                  hover>
                  <TableCell>{customer.name}</TableCell>
                  <TableCell>{customer.sector}</TableCell>
                  <TableCell>{customer.operatingModel}</TableCell>
                  <TableCell>{customer.sitesCount}</TableCell>
                  <TableCell>{customer.owner}</TableCell>
                  <TableCell>
                    <Chip label={customer.status}
                      size="small"
                      color={customer.status === 'Renewal Due' ? 'warning' : customer.status === 'Onboarding' ? 'default' : 'success'}
                      variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small"
                      title={t('allCustomersOpenWorkspace', 'Open client workspace')}
                      onClick={() => navigate(`/customer-management/customers/${customer.id}`)}>
                      <ArrowForward fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomPagination page={page}
          setPage={setPage}
          maxPages={1}
          useNoDataMessage={false}
          breakpointForChangeDisplay={120000} />
      </Section>
    </PageArea>
  );
};

export default AllCustomers;
